<template>
  <div :class="getBlockPadding(additionalSpacing)">
    <Background :background>
      <div
        class="flex w-full flex-col gap-24 md:max-w-[1120px]"
        :class="getPaddingClass({ background })"
      >
        <div class="flex flex-col gap-8">
          <RichText
            v-if="title"
            class="text-center md:line-clamp-2 [[lang='ja-jp']_&]:md:leading-[4.5rem]"
            :h2-style="tw`punchline`"
            :node-renderers-overrides="noEmptyParagraphs"
            :rich-text="title"
            style-variant="none"
          />

          <RichText
            v-if="description"
            class="heading-3 text-center"
            :node-renderers-overrides="noEmptyParagraphs"
            :rich-text="description"
            style-variant="none"
            :tracking="trackingData"
          />
        </div>

        <CmsButton
          v-if="button"
          class="m-auto"
          data-test="section-banner-cta-button"
          v-bind="button"
          full-width="adaptive"
          :tracking="trackingData"
        />

        <RelatedLinks
          v-if="relatedLinks"
          :align="relatedLinks.align"
          :display="relatedLinks.display"
          :links="relatedLinks.links"
          :size="relatedLinks.size"
          :tracking="trackingData"
        />
      </div>
    </Background>
  </div>
</template>

<script setup lang="ts">
import { h, onMounted, ref } from 'vue'

import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import { tw } from '@backmarket/utils/string/tw'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import type { NodeRendererMap } from 'contentful-rich-text-vue-renderer'

import Background from '../../shared-components/Background/Background.vue'
import CmsButton from '../Button/Button.vue'
import RelatedLinks from '../RelatedLinks/RelatedLinks.vue'
import RichText from '../RichText/RichText.vue'

import type { SectionBannerBlockProps } from './SectionBanner.types'
import { getBlockPadding, getPaddingClass } from './SectionBanner.utils'

const props = withDefaults(defineProps<SectionBannerBlockProps>(), {
  additionalSpacing: 'None',
})

const trackingData = ref<Tracking>()

// In some cases, Contentful might add empty paragraphs in the title/description.
// Since these are clamped to 2 lines, it might show an unnecessary ellipsis
// Hiding empty paragraphs fixes this issue
const noEmptyParagraphs: NodeRendererMap = {
  paragraph: (node, key, next) => {
    return h(
      'p',
      { key, class: tw`empty:hidden` },
      next(node.content, key, next),
    )
  },
}

// trackingData is only used on the client-side
// we do not need to compute a potentially heavy documentToPlainTextString server-side
onMounted(() => {
  trackingData.value = {
    ...props.tracking,
    ...(props.title
      ? { blockTitle: documentToPlainTextString(props.title) }
      : {}),
  }
})

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error'])
</script>
